import i18n, { t } from "i18next";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { MagicStar } from "iconsax-react";
import { Spin } from "antd";
import DBModal from "../../component/global/DBModal";
import DBButton from "../../component/global/DBButton";
import DBFieldBlock from "../../component/global/DBFieldBlock";
import DBInput from "../../component/global/DBInput";
import DBSelect from "../../component/global/DBSelect";
import DBAutoComplete from "../../component/global/DBAutoComplete";
import DBTextArea from "../../component/global/DBTextArea";
import { ToastContext } from "../../component/global/ToastContext";
import { GlobalStateContext } from "../../layouts/GlobalStates";
import { get } from "../../controlers/global/ConfigRequest";
import { useRequest } from "../../hooks/helperData/useRequest";
import JalaliDateTimeInput from "../../component/global/JalaliDateTimeInput";
import {
    SURVEY_CAMPAIGNS,
    SURVEY_CAMPAIGNS_AI_GENERATE_MESSAGE,
    SURVEY_LIST,
    SURVEY_SENDER_PROFILES,
    COMPANY_DETAIL,
    COMPANY_LIST_URL,
    COMPANY_MEMBERS,
    surveyCampaignUrl,
} from "../../helperData/ApiAddress";
import {
    CAMPAIGN_KIND,
    CAMPAIGN_STATUS,
    SENDER_CHANNEL,
    SMS_MESSAGE_MAX_CHARS,
    ORG_NAME_POSITION,
    SURVEY_CHANNEL,
    SURVEY_STATUS,
    campaignIsEditable,
    campaignKindOptions,
    campaignKindSelectValue,
    composeCampaignMessageBody,
    defaultPhoneSettings,
    isPhoneKind,
    isPhoneSurvey,
    messageTypeOptions,
    organizationNamePositionOptions,
    parseCampaignKindSelect,
    PHONE_INTERVIEW,
} from "./constants";
import CampaignRecipientsSection, {
    ADDRESS_CAMPAIGN_KINDS,
} from "./CampaignRecipientsSection";
import PhoneSettingsFields from "./PhoneSettingsFields";
import { translateServerDetail } from "./translateServerDetail";
import { isInsufficientCoins, useAiCoinGate, AiCoinHint } from "../../features/ai-billing/AiCoinGate";

const KIND_TO_CHANNEL = {
    [CAMPAIGN_KIND.EMAIL]: SENDER_CHANNEL.EMAIL,
    [CAMPAIGN_KIND.SMS]: SENDER_CHANNEL.SMS,
    [CAMPAIGN_KIND.IN_APP]: SENDER_CHANNEL.IN_APP,
    [CAMPAIGN_KIND.USERS]: SENDER_CHANNEL.IN_APP,
};

const defaultForm = (surveyId, organizationName = "", channel) => ({
    survey: surveyId || null,
    name: "",
    kind:
        channel === SURVEY_CHANNEL.PHONE
            ? CAMPAIGN_KIND.PHONE
            : CAMPAIGN_KIND.LINK,
    user_group_type: "",
    message_type: 1,
    message_subject: "",
    message_body: "",
    organization_name: organizationName,
    organization_name_position: ORG_NAME_POSITION.END,
    sender_profile: null,
    scheduled_at: "",
    phone_config: defaultPhoneSettings(),
    operator_ids: [],
});

const formFromCampaign = (surveyId, campaign, fallbackOrgName = "") => ({
    survey: surveyId || campaign.survey || null,
    name: campaign.name,
    kind: campaign.kind,
    user_group_type: campaign.user_group_type || "",
    message_type: campaign.message_type,
    message_subject: campaign.message_subject || "",
    message_body: campaign.message_body || "",
    organization_name:
        campaign.organization_name == null
            ? fallbackOrgName
            : campaign.organization_name,
    organization_name_position:
        campaign.organization_name_position || ORG_NAME_POSITION.END,
    sender_profile: campaign.sender_profile,
    scheduled_at: campaign.scheduled_at
        ? campaign.scheduled_at.slice(0, 16)
        : "",
    phone_config: campaign.phone_config || defaultPhoneSettings(),
    operator_ids: campaign.phone_config?.operator_ids || [],
});

const loadCompanyName = (companyId) => {
    if (companyId) {
        return get(COMPANY_DETAIL.replace("{id}", companyId))
            .then((r) => r?.name || "")
            .catch(() => "");
    }
    return get(COMPANY_LIST_URL)
        .then((r) => r?.results?.[0]?.name || "")
        .catch(() => "");
};

export default function CampaignModal({
    show,
    survey,
    campaign,
    onHide,
    onSuccess,
    stacked,
    mode = "survey",
    linkOnly = false,
}) {
    const toast = useContext(ToastContext);
    const { auth } = useContext(GlobalStateContext) || {};
    const [savedCampaign, setSavedCampaign] = useState(campaign || null);
    const [form, setForm] = useState(defaultForm(survey?.id));
    const [companyName, setCompanyName] = useState("");
    const [errors, setErrors] = useState({});
    const [senderProfiles, setSenderProfiles] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [saveLoading, setSaveLoading] = useState(false);
    const [members, setMembers] = useState([]);
    const [phoneSurveys, setPhoneSurveys] = useState([]);

    const [getSenderProfiles] = useRequest(SURVEY_SENDER_PROFILES, "GET");
    const [getCampaigns] = useRequest(SURVEY_CAMPAIGNS, "GET");
    const [getSurveys] = useRequest(SURVEY_LIST, "GET");
    const unboundCreate = !survey?.id && mode !== "content" && !linkOnly;
    const [generateMessage, generateLoading] = useRequest(
        SURVEY_CAMPAIGNS_AI_GENERATE_MESSAGE,
        "POST"
    );
    const { confirmAndRun, openFromError, gate } = useAiCoinGate();

    useEffect(() => {
        if (!show) return;
        setErrors({});
        setSavedCampaign(campaign || null);
        if (!linkOnly) {
            getSenderProfiles({ page_size: 100 })
                .then((r) =>
                    setSenderProfiles(
                        (r.results || r || []).map((profile) => ({
                            value: profile.id,
                            label: `${profile.name} (${profile.channel_display})`,
                            channel: profile.channel,
                        }))
                    )
                )
                .catch(() => setSenderProfiles([]));
        } else {
            setSenderProfiles([]);
        }
        getCampaigns({
            page_size: 100,
            ...(linkOnly ? { scope: "personal" } : {}),
        })
            .then((r) => setCatalog(r.results || []))
            .catch(() => setCatalog([]));
        const companyId =
            auth?.usercompany || window.sessionStorage.usercompany || "";
        if (campaign?.id) {
            setForm(formFromCampaign(survey?.id, campaign, companyName));
        } else {
            setForm(
                defaultForm(
                    survey?.id,
                    linkOnly ? "" : companyName,
                    survey?.channel
                )
            );
        }
        if (!linkOnly) {
            loadCompanyName(companyId).then((name) => {
                setCompanyName(name);
                setForm((current) =>
                    current.organization_name
                        ? current
                        : { ...current, organization_name: name }
                );
            });
        }
    }, [show, campaign?.id, survey?.id]);

    useEffect(() => {
        if (!show || linkOnly) {
            setMembers([]);
            return;
        }
        const companyId =
            auth?.usercompany || window.sessionStorage.usercompany || "";
        axios
            .get(COMPANY_MEMBERS, {
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem("auth")}`,
                    "Accept-Language": i18n.language || "fa",
                },
                params: {
                    page_size: 200,
                    company: companyId || undefined,
                },
            })
            .then((r) => {
                const rows = Array.isArray(r.data)
                    ? r.data
                    : r.data?.results || [];
                setMembers(rows);
            })
            .catch(() => setMembers([]));
    }, [show, linkOnly, auth?.usercompany]);

    useEffect(() => {
        if (!show || !unboundCreate) {
            setPhoneSurveys([]);
            return;
        }
        getSurveys({
            channel: SURVEY_CHANNEL.PHONE,
            page_size: 100,
        })
            .then((r) =>
                setPhoneSurveys(
                    (r.results || []).filter(
                        (row) => row.status !== SURVEY_STATUS.ARCHIVED
                    )
                )
            )
            .catch(() => setPhoneSurveys([]));
    }, [show, unboundCreate]);

    const authHeaders = () => ({
        Authorization: `Bearer ${sessionStorage.getItem("auth")}`,
        "Accept-Language": i18n.language || "fa",
    });

    const persistCampaign = ({ silent = false, keepOpen } = {}) => {
        if (!form.name.trim()) {
            setErrors({ name: t("survey.campaign.name.required") });
            return Promise.reject(new Error("name"));
        }
        const phoneKind = isPhoneKind(form.kind);
        const interviewMode = form.phone_config?.interview_mode;
        const operatorsNeeded =
            phoneKind &&
            (interviewMode === PHONE_INTERVIEW.OPERATOR ||
                interviewMode === PHONE_INTERVIEW.MIXED);
        if (phoneKind && !(survey?.id || form.survey)) {
            setErrors({ survey: t("survey.campaign.survey.required") });
            return Promise.reject(new Error("survey"));
        }
        if (operatorsNeeded && !(form.operator_ids || []).length) {
            setErrors({
                operator_ids: t("survey.phone.operators.required"),
            });
            return Promise.reject(new Error("operator_ids"));
        }
        const payload = {
            ...form,
            name: form.name.trim(),
            organization_name: (form.organization_name || "").trim(),
            survey:
                mode === "content"
                    ? savedCampaign?.survey || null
                    : survey?.id || form.survey || null,
            sender_profile: phoneKind ? null : form.sender_profile || null,
            scheduled_at: form.scheduled_at || null,
            user_group_type:
                form.kind === CAMPAIGN_KIND.USERS
                    ? form.user_group_type || ""
                    : "",
        };
        if (phoneKind) {
            payload.phone_config = { ...(form.phone_config || {}) };
            payload.operator_ids = form.operator_ids || [];
        } else {
            delete payload.phone_config;
            delete payload.operator_ids;
        }
        setSaveLoading(true);
        const request = savedCampaign?.id
            ? axios.put(`${SURVEY_CAMPAIGNS}${savedCampaign.id}/`, payload, {
                  headers: authHeaders(),
              })
            : axios.post(SURVEY_CAMPAIGNS, payload, {
                  headers: authHeaders(),
              });
        return request
            .then((r) => {
                const next = r.data;
                setSavedCampaign(next);
                if (!silent) {
                    toast(t("survey.campaign.save.success"), "success");
                }
                const shouldKeepOpen =
                    keepOpen ??
                    (ADDRESS_CAMPAIGN_KINDS.includes(next.kind) &&
                        !campaign?.id);
                onSuccess?.(next, {
                    keepOpen: Boolean(shouldKeepOpen || silent),
                });
                return next;
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                toast(detail || t("survey.campaign.save.error"), "danger");
                return Promise.reject(e);
            })
            .finally(() => setSaveLoading(false));
    };

    const ensureCampaignSaved = () => {
        if (savedCampaign?.id) return Promise.resolve(savedCampaign);
        return persistCampaign({ silent: true, keepOpen: true });
    };

    const fillFromCampaign = (row) => {
        if (!row) return;
        setSavedCampaign(row);
        setForm(formFromCampaign(survey?.id, row, companyName));
        setErrors({});
    };

    const handleCatalogSelect = (option) => {
        const source =
            option?.campaign ||
            catalog.find((row) => row.name === option?.value);
        if (!source) return;
        if (survey?.id && source.survey && source.survey !== survey.id) {
            axios
                .post(
                    surveyCampaignUrl(source.id, "apply"),
                    { survey: survey.id },
                    { headers: authHeaders() }
                )
                .then((r) => fillFromCampaign(r.data))
                .catch((e) => {
                    const detail = translateServerDetail(
                        e.response?.data?.detail,
                        t
                    );
                    toast(detail || t("survey.campaign.save.error"), "danger");
                });
            return;
        }
        fillFromCampaign(source);
    };

    const handleNameChange = (value) => {
        const nextName = value || "";
        setForm((current) => ({ ...current, name: nextName }));
        const matched = catalog.find((row) => row.name === nextName);
        if (!matched && savedCampaign?.id && savedCampaign.name !== nextName) {
            setSavedCampaign(null);
        }
    };

    const nameOptions = catalog.map((row) => ({
        value: row.name,
        label: row.content_title
            ? `${row.name} · ${row.content_title}`
            : row.survey_title
            ? `${row.name} · ${row.survey_title}`
            : row.name,
        campaign: row,
    }));
    const channel = KIND_TO_CHANNEL[form.kind];
    const phoneKind = isPhoneKind(form.kind);
    const filteredProfiles = channel
        ? senderProfiles.filter((profile) => profile.channel === channel)
        : senderProfiles;
    const showRecipientsSection = ADDRESS_CAMPAIGN_KINDS.includes(
        savedCampaign?.kind ?? form.kind
    );
    const canEditCampaign = campaignIsEditable(savedCampaign);
    const isSmsChannel = form.kind === CAMPAIGN_KIND.SMS;
    const memberOptions = members.map((member) => {
        const name = [member.first_name, member.last_name]
            .filter(Boolean)
            .join(" ");
        return {
            value: member.id,
            label: name || member.username || String(member.phone_number || member.id),
        };
    });
    const composedMessage = composeCampaignMessageBody(
        form.message_body,
        form.organization_name,
        form.organization_name_position
    );
    const messageLength = composedMessage.length;
    const smsOverLimit = isSmsChannel && messageLength > SMS_MESSAGE_MAX_CHARS;

    const handleGenerateMessage = () => {
        if (!canEditCampaign || generateLoading || !survey?.id) return;
        confirmAndRun("campaign_message", () => {
            generateMessage({
                survey: survey.id,
                kind: form.kind,
                campaign_name: form.name,
            })
                .then((r) => {
                    setForm((current) => ({
                        ...current,
                        message_body: r.message_body || "",
                    }));
                })
                .catch((e) => {
                    if (isInsufficientCoins(e)) {
                        openFromError(e);
                        return;
                    }
                    const detail = translateServerDetail(
                        e.response?.data?.detail,
                        t
                    );
                    toast(
                        detail || t("survey.campaign.message.ai.error"),
                        "danger"
                    );
                });
        });
    };
    const title = savedCampaign?.id
        ? t(
              mode === "content"
                  ? "contentStudio.send.editCampaign"
                  : "survey.campaign.edit"
          )
        : t(
              mode === "content"
                  ? "contentStudio.send.createCampaign"
                  : "survey.campaign.create"
          );

    return (
        <>
        <DBModal
            permanent
            stacked={stacked}
            show={show}
            minWidth={showRecipientsSection ? 680 : 560}
            onHide={onHide}
            className="survey-form-modal campaign-modal"
        >
            <DBModal.Header
                closeBtn={onHide}
                closeLabel={t("survey.settings.help.close")}
                title={title}
            />
            <DBModal.Content>
                {!savedCampaign?.id && (
                    <p className="survey-form-help">
                        {t(
                            linkOnly
                                ? "survey.campaign.intro.link"
                                : "survey.campaign.intro"
                        )}
                    </p>
                )}
                <div className="survey-form-grid">
                    <DBFieldBlock
                        title={t("survey.campaign.name")}
                        hint={t("survey.campaign.name.hint")}
                        required
                        error={errors.name}
                    >
                        <DBAutoComplete
                            status={errors.name && "error"}
                            disabled={!canEditCampaign}
                            value={form.name}
                            options={nameOptions}
                            placeholder={t("search.and.select")}
                            onChange={handleNameChange}
                            onSelect={(_value, option) =>
                                handleCatalogSelect(option)
                            }
                            onLock={(_caption, option) =>
                                handleCatalogSelect(option)
                            }
                        />
                    </DBFieldBlock>
                    <DBFieldBlock
                        title={t("survey.campaign.kind")}
                        hint={
                            phoneKind
                                ? t("survey.campaign.kind.phone.hint")
                                : t("survey.campaign.kind.hint")
                        }
                        required
                    >
                        <DBSelect
                            size="large"
                            aria-label={t("survey.campaign.kind")}
                            disabled={
                                Boolean(savedCampaign?.id) || !canEditCampaign
                            }
                            value={campaignKindSelectValue(
                                form.kind,
                                form.user_group_type
                            )}
                            onChange={(value) => {
                                const parsed = parseCampaignKindSelect(value);
                                const nextPhone = isPhoneKind(parsed.kind);
                                setForm({
                                    ...form,
                                    kind: parsed.kind,
                                    user_group_type: parsed.user_group_type,
                                    sender_profile: null,
                                    survey: nextPhone
                                        ? form.survey ||
                                          (phoneSurveys.length === 1
                                              ? phoneSurveys[0].id
                                              : form.survey)
                                        : unboundCreate
                                          ? null
                                          : survey?.id || form.survey,
                                });
                            }}
                            options={campaignKindOptions(form.kind, {
                                linkOnly,
                                phoneSurvey: isPhoneSurvey(survey),
                                allowPhone: unboundCreate,
                            })}
                        />
                    </DBFieldBlock>
                    {phoneKind && unboundCreate && (
                    <DBFieldBlock
                        title={t("survey.campaign.survey")}
                        hint={
                            phoneSurveys.length
                                ? t("survey.campaign.survey.hint")
                                : t("survey.campaign.survey.empty")
                        }
                        required
                        error={errors.survey}
                    >
                        <DBSelect
                            size="large"
                            aria-label={t("survey.campaign.survey")}
                            disabled={!canEditCampaign || !phoneSurveys.length}
                            status={errors.survey && "error"}
                            value={form.survey}
                            onChange={(value) =>
                                setForm({
                                    ...form,
                                    survey: value || null,
                                })
                            }
                            options={phoneSurveys.map((row) => ({
                                value: row.id,
                                label: row.title,
                            }))}
                            placeholder={t("search.and.select")}
                        />
                    </DBFieldBlock>
                    )}
                    {!phoneKind && (
                    <>
                    <DBFieldBlock
                        title={t("survey.campaign.message.type")}
                        hint={t("survey.campaign.message.type.hint")}
                    >
                        <DBSelect
                            size="large"
                            aria-label={t("survey.campaign.message.type")}
                            disabled={!canEditCampaign}
                            value={form.message_type}
                            onChange={(value) =>
                                setForm({ ...form, message_type: value })
                            }
                            options={messageTypeOptions()}
                        />
                    </DBFieldBlock>
                    {channel && (
                        <DBFieldBlock
                            title={t("survey.campaign.sender.profile")}
                            hint={t("survey.campaign.sender.profile.hint")}
                        >
                            <DBSelect
                                size="large"
                                allowClear
                                aria-label={t("survey.campaign.sender.profile")}
                                disabled={!canEditCampaign}
                                value={form.sender_profile}
                                onChange={(value) =>
                                    setForm({
                                        ...form,
                                        sender_profile: value ?? null,
                                    })
                                }
                                options={filteredProfiles}
                            />
                        </DBFieldBlock>
                    )}
                    {form.kind === CAMPAIGN_KIND.EMAIL && (
                        <DBFieldBlock
                            title={t("survey.campaign.message.subject")}
                            hint={t("survey.campaign.message.subject.hint")}
                            span={2}
                        >
                            <DBInput
                                size="large"
                                name="message_subject"
                                autoComplete="off"
                                spellCheck={false}
                                disabled={!canEditCampaign}
                                value={form.message_subject}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        message_subject: e.target.value,
                                    })
                                }
                            />
                        </DBFieldBlock>
                    )}
                    <DBFieldBlock
                        title={t("survey.campaign.message.body")}
                        hint={t("survey.campaign.message.body.hint")}
                        span={2}
                    >
                        <div className="campaign-message-field">
                            {isSmsChannel && (
                                <p
                                    className={`campaign-message-limit${
                                        smsOverLimit ? " is-over" : ""
                                    }`}
                                    aria-live="polite"
                                >
                                    {t(
                                        "survey.campaign.message.body.sms.limit",
                                        {
                                            current: messageLength,
                                            max: SMS_MESSAGE_MAX_CHARS,
                                        }
                                    )}
                                </p>
                            )}
                            <div className="campaign-message-editor">
                                <DBTextArea
                                    rows={3}
                                    name="message_body"
                                    autoComplete="off"
                                    disabled={!canEditCampaign}
                                    value={form.message_body}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            message_body: e.target.value,
                                        })
                                    }
                                />
                                {canEditCampaign && survey?.id && (
                                    <button
                                        type="button"
                                        className="campaign-message-ai"
                                        onClick={handleGenerateMessage}
                                        disabled={generateLoading}
                                        aria-busy={generateLoading}
                                        aria-label={t(
                                            "survey.campaign.message.ai.generate"
                                        )}
                                        title={t(
                                            "survey.campaign.message.ai.generate"
                                        )}
                                    >
                                        {generateLoading ? (
                                            <Spin size="small" />
                                        ) : (
                                            <MagicStar
                                                size={18}
                                                variant="Bold"
                                                aria-hidden="true"
                                            />
                                        )}
                                        <AiCoinHint
                                            operation="campaign_message"
                                            mode="overlay"
                                        />
                                    </button>
                                )}
                            </div>
                        </div>
                    </DBFieldBlock>
                    {!linkOnly && (
                    <DBFieldBlock
                        title={t("survey.campaign.organization.name")}
                        hint={t("survey.campaign.organization.name.hint")}
                        span={2}
                    >
                        <div className="campaign-org-name-row">
                            <DBInput
                                size="large"
                                name="organization_name"
                                autoComplete="organization"
                                spellCheck={false}
                                disabled={!canEditCampaign}
                                value={form.organization_name}
                                placeholder={companyName}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        organization_name: e.target.value,
                                    })
                                }
                            />
                            <DBSelect
                                size="large"
                                aria-label={t(
                                    "survey.campaign.organization.position"
                                )}
                                disabled={!canEditCampaign}
                                value={form.organization_name_position}
                                onChange={(value) =>
                                    setForm({
                                        ...form,
                                        organization_name_position: value,
                                    })
                                }
                                options={organizationNamePositionOptions()}
                            />
                        </div>
                    </DBFieldBlock>
                    )}
                    </>
                    )}
                    {phoneKind && (
                    <>
                    <DBFieldBlock
                        title={t("survey.phone.operators")}
                        hint={t("survey.phone.operators.hint")}
                        required
                        error={errors.operator_ids}
                        span={2}
                    >
                        <DBSelect
                            size="large"
                            mode="multiple"
                            aria-label={t("survey.phone.operators")}
                            disabled={!canEditCampaign}
                            value={form.operator_ids}
                            onChange={(value) =>
                                setForm({
                                    ...form,
                                    operator_ids: value || [],
                                })
                            }
                            options={memberOptions}
                            placeholder={t("search.and.select")}
                        />
                    </DBFieldBlock>
                    <div className="phone-campaign-settings">
                        <PhoneSettingsFields
                            compact
                            disabled={!canEditCampaign}
                            value={form.phone_config}
                            onChange={(phone_config) =>
                                setForm({ ...form, phone_config })
                            }
                        />
                    </div>
                    </>
                    )}
                    <DBFieldBlock
                        title={t("survey.campaign.scheduled")}
                        hint={t("survey.campaign.scheduled.hint")}
                    >
                        <JalaliDateTimeInput
                            disabled={!canEditCampaign}
                            value={form.scheduled_at}
                            onChange={(next) =>
                                setForm({
                                    ...form,
                                    scheduled_at: next,
                                })
                            }
                        />
                    </DBFieldBlock>
                </div>

                {showRecipientsSection && (
                    <CampaignRecipientsSection
                        campaign={
                            savedCampaign || {
                                kind: form.kind,
                                status: CAMPAIGN_STATUS.DRAFT,
                                user_group_type: form.user_group_type,
                            }
                        }
                        onEnsureCampaign={ensureCampaignSaved}
                        onChange={() =>
                            onSuccess?.(savedCampaign, { keepOpen: true })
                        }
                    />
                )}
            </DBModal.Content>
            <DBModal.Footer>
                {canEditCampaign && (
                    <DBButton
                        caption={t("save")}
                        onClick={() => persistCampaign().catch(() => {})}
                        loading={saveLoading}
                    />
                )}
                <DBButton
                    caption={t("cancel")}
                    type="secondary"
                    onClick={onHide}
                />
            </DBModal.Footer>
        </DBModal>
        {gate}
        </>
    );
}
